import axios from 'axios';
import { BACKEND_URL } from '../config';

const baseURL = `${BACKEND_URL}/api`;
export const api = axios.create({ baseURL, withCredentials: true });

// Add session_id from localStorage as Authorization header (for iframe context)
// Cookies don't work in iframes due to browser privacy restrictions
api.interceptors.request.use((config) => {
  const sessionId = localStorage.getItem('pos_session_id');
  console.log('[API Client] Interceptor - localStorage session_id:', sessionId ? `YES (${sessionId.substring(0, 20)}...)` : 'NO');
  if (sessionId) {
    config.headers.Authorization = `Bearer ${sessionId}`;
    console.log('[API Client] Added Authorization header');
  } else {
    console.warn('[API Client] No session_id in localStorage - request will fail!');
  }
  return config;
});

// Track 401 errors to avoid clearing session too quickly
let consecutive401Count = 0;
let last401Time = 0;

// Global 401 handler: redirect to login if session expired (e.g., after backend restart)
// BUT: Only clear session if 401 comes from OUR backend, not external APIs
api.interceptors.response.use(
  (res) => {
    // Reset 401 counter on successful response
    consecutive401Count = 0;
    return res;
  },
  (err) => {
    const status = err?.response?.status;
    const currentPath = window.location.pathname;
    const isAuthPage = currentPath === '/login' || currentPath === '/oauth/callback';
    const requestUrl = err?.config?.url || '';
    const errorDetail = err?.response?.data?.detail || '';
    
    // Only clear session if the 401 is from OUR backend API (not external APIs like GHL)
    const isOurBackend = requestUrl.startsWith('/') || requestUrl.includes(BACKEND_URL);
    
    if (status === 401 && !isAuthPage && isOurBackend) {
      console.warn('[API Client] 401 from our backend:', requestUrl);
      console.warn('[API Client] Error detail:', errorDetail);
      
      // Track consecutive 401s
      const now = Date.now();
      if (now - last401Time < 5000) { // Within 5 seconds
        consecutive401Count++;
      } else {
        consecutive401Count = 1; // Reset if more than 5 seconds apart
      }
      last401Time = now;
      
      console.log(`[API Client] Consecutive 401 count: ${consecutive401Count}`);
      
      // Check if error message indicates we're in iframe and should retry SSO
      const isInIframe = window.self !== window.top;
      const shouldRetrySSO = isInIframe && errorDetail.includes('Not connected to GoHighLevel');
      
      if (shouldRetrySSO && consecutive401Count < 3) {
        // Don't clear session yet - let user try to reconnect or reload
        console.warn('[API Client] 401 in iframe - user should reload to trigger SSO');
        // Don't clear localStorage - keep session for recovery
        return Promise.reject(err);
      }
      
      // Only clear session after multiple consecutive 401s
      if (consecutive401Count >= 3) {
        console.error('[API Client] Multiple 401 errors - clearing session and redirecting to login');
        localStorage.removeItem('pos_session_id');
        try { sessionStorage.setItem('last_401_path', currentPath); } catch {}
        
        if (isInIframe) {
          // In iframe, reload to trigger SSO instead of redirecting
          window.location.reload();
        } else {
          window.location.href = '/login';
        }
      } else {
        console.warn(`[API Client] 401 error #${consecutive401Count} - not clearing session yet`);
        // Don't clear session on first 401 - might be temporary
      }
    } else if (status === 401 && !isOurBackend) {
      console.warn('[API Client] 401 from external API - not clearing session, just returning error');
    }
    return Promise.reject(err);
  }
);
